import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, Injectable, Input, OnInit, Output, SimpleChanges, EventEmitter } from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { BehaviorSubject, Observable, of as observableOf } from 'rxjs';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { SelectionModel } from '@angular/cdk/collections';
import { EnquiryRegService } from 'src/app/services/services/enquiry-reg.service';
import { TreeService } from 'src/app/services/services/tree.service';
import { MatDialog } from '@angular/material/dialog';
import { I18NextPipe } from 'angular-i18next';
import { AddNodeDialogComponent } from '../enquiry-registration-dialogs/add-node-dialog/add-node-dialog.component';
import { DeleteSubCategoryDialogComponent } from '../enquiry-registration-dialogs/delete-sub-category-dialog/delete-sub-category-dialog.component';
import * as _ from 'lodash';
import { EditNodeDialogComponent } from '../enquiry-registration-dialogs/edit-node-dialog/edit-node-dialog.component';
import { WarningDialogComponent } from '../enquiry-registration-dialogs/warning-dialog/warning-dialog.component';

/**
 * File node data with nested structure.
 * Each node has a filename, and a type or a list of children.
 */
export class FileNode {
  public externalId: number;
  public parentNodeId: number;
  public description: string;
  public level: number;
  public order: number;
  public children: FileNode[];
  public isNew: boolean = false;
  public isUpdated: boolean = false;
  public isDeleted: boolean = false;
  public updateToCategory: boolean = false;
  public downgradeToTopic: boolean = false;
  public isMultiSelect: number;
  public isDefault: number;
  public isOutbound: number;
  public isForced: number;
  public isSensitive: number;
  public storageTime: number;
  public type: string;
}

/** Flat node with expandable and level information */
export class FileFlatNode {
  public expandable: boolean;
  public externalId: number;
  public parentNodeId: number;
  public description: string;
  public level: number;
  public order: number;
  public isNew: boolean = false;
  public isUpdated: boolean = false;
  public isDeleted: boolean = false;
  public updateToCategory: boolean = false;
  public downgradeToTopic: boolean = false;
  public isMultiSelect: number;
  public isDefault: number;
  public isOutbound: number;
  public isForced: number;
  public isSensitive: number;
  public storageTime: number;
  public type: string;
}

export enum GlobalSettings {
  Ordinary = 0,
  Default = 1,
  Dialler = 2
}

/**
 * The file structure tree data in string. The data could be parsed into a Json object
 */

/**
 * File database, it can build a tree structured Json object from string.
 * Each node in Json object represents a file or a directory. For a file, it has filename and type.
 * For a directory, it has filename and children (a list of files or directories).
 * The input will be a json object string, and the output is a list of `FileNode` with nested
 * structure.
 */
@Injectable()
export class FileDatabase {
  dataChange = new BehaviorSubject<FileNode[]>([]);
  initData: FileNode[] = [];

  // set data(data: any) {
  //   this.dataChange.next(data);
  // }

  get data(): FileNode[] { return this.dataChange.value; }

  constructor() {
    // this.initialize();
  }

  initialize(treeData, mainCategory) {
    let formattedTreeData: FileNode[] = [];
    let levels = this.getUniqueValues(treeData, "hierarchyLevel");
    for (let i = 0; i < levels.length; i++) {
      let level = levels[i];
      let categories = treeData.filter(td => td["hierarchyLevel"] === level);
      if (level === 0) {
        for (let i = 0; i < categories.length; i++) {
          let description = categories[i]["description"];
          let nodeId = categories[i]["nodeId"];
          let isMultiSelect = mainCategory["isMultiSelect"] ? 1 : 0;
          let isDefault = mainCategory["isDefault"] ? 1 : 0;
          let isOutbound = mainCategory["isOutbound"] ? 1 : 0;
          let isSensitive = mainCategory["isSensitive"] ? 1 : 0;
          let storageTime = mainCategory["storageTime"];
          let order = mainCategory["categoryNum"];
          let isForced = mainCategory["isForced"];
          let newFileNode: FileNode = {
            externalId: nodeId,
            parentNodeId: -1,
            description: description,
            level: 0,
            order: order,
            children: [],
            isNew: false,
            isUpdated: false,
            isDeleted: false,
            updateToCategory: false,
            downgradeToTopic: false,
            isSensitive: isSensitive,
            storageTime: storageTime,
            isMultiSelect: isMultiSelect,
            isDefault: isDefault,
            isOutbound: isOutbound,
            isForced: isForced,
            type: categories[i].type
          }

          formattedTreeData.push(newFileNode);
        }
      } else {
        for (let i = 0; i < categories.length; i++) {
          let node = categories[i];
          this.findNodeRecrusively(formattedTreeData, node);
        }
      }
    }

    this.initData = _.cloneDeep(formattedTreeData);

    // Notify the change.
    this.dataChange.next(formattedTreeData);
  }

  findNodeRecrusively(arr, node) {
    let id = node.nodeOrder;
    let externalId = node.nodeId;
    let parentNodeId = node.parentNodeId;
    let description = node.description;
    let level = node.hierarchyLevel;
    let isSensitive = node.isSensitive ? 1 : 0;
    let storageTime = node.storageTime;
    for (let i = 0; i < arr.length; i++) {
      let arrNode = arr[i];
      if (arrNode.externalId === parentNodeId) {
        let newFileNode: FileNode = {
          externalId: externalId,
          parentNodeId: parentNodeId,
          description: description,
          level: level,
          order: arrNode.children.length + 1,
          children: [],
          isNew: false,
          isUpdated: false,
          isDeleted: false,
          updateToCategory: false,
          downgradeToTopic: false,
          isMultiSelect: node.isMultiSelect ? 1 : 0,
          isDefault: 0,
          isOutbound: 0,
          isForced: node.isForced,
          isSensitive: isSensitive,
          storageTime: storageTime,
          type: node.type
        };
        arrNode.children.push(newFileNode);

        break;
      } else {
        this.findNodeRecrusively(arrNode.children, node);
      }
    }
  }

  getUniqueValues(array, prop) {
    let unique_values = array
      .map((item) => item[prop])
      .filter(
        (value, index, current_value) => current_value.indexOf(value) === index
      );
    return unique_values;
  }

  findNodeById(id, children, newItem) {
    for (let i = 0; i < children.length; i++) {
      let node = children[i];
      if (node.externalId === id) {
        node.children.push(newItem);
        break;
      }

      if (node.children.length > 0) {
        this.findNodeById(id, node.children, newItem);
      }
    }
  }

  editNodeById(id, children, description, isSensitive, storageTime) {
    for (let i = 0; i < children.length; i++) {
      let node = children[i];
      if (node.externalId === id) {
        node.description = description;
        node.isSensitive = isSensitive ? 1 : 0;
        node.storageTime = storageTime;
        break;
      }

      if (node.children.length > 0) {
        this.editNodeById(id, node.children, description, isSensitive, storageTime);
      }
    }
  }

  deleteNodeById(id, node) {
    for (let i = 0; i < node.children.length; i++) {
      let currentNode = node.children[i];
      if (currentNode.externalId === id) {
        // Delete index of node
        let nodeToRemove = node.children.find(c => c.externalId === currentNode.externalId);
        let index = node.children.indexOf(nodeToRemove);
        node.children.splice(index, 1);

        // Update order of children after delete
        if (node.children.length > 0) {
          for (let j = 0; j < node.children.length; j++) {
            let childNode = node.children[j];
            childNode.order = j + 1;
          }
        }
        break;
      }

      if (currentNode.children.length > 0) {
        this.deleteNodeById(id, currentNode);
      }
    }
  }

  insertNode(parentNode: FileNode, description: string, externalId: number): FileNode {
    let parentId = parentNode.externalId;

    let newItem: FileNode = {
      externalId: externalId,
      parentNodeId: parentNode.externalId,
      description: description,
      level: parentNode.level + 1,
      order: parentNode.children.length + 1,
      children: [],
      isNew: true,
      isUpdated: false,
      isDeleted: false,
      updateToCategory: false,
      downgradeToTopic: false,
      isMultiSelect: 0,
      isDefault: 0,
      isOutbound: 0,
      isForced: 0,
      isSensitive: 0,
      storageTime: null,
      type: ""
    }

    if (this.data[0].externalId === parentId) {
      this.data[0].children.push(newItem);
    } else {
      this.findNodeById(parentId, this.data[0].children, newItem);
    }

    this.dataChange.next(this.data);

    return newItem;
  }

  editNode(id: number, description: string, isSensitive: number, storageTime: number) {
    if (id === this.data[0].externalId) {
      this.data[0].description = description;
    } else {
      this.editNodeById(id, this.data[0].children, description, isSensitive, storageTime);
    }

    this.dataChange.next(this.data);
  }

  deleteNodeAndChildren(node: FileNode) {
    this.deleteNodeById(node.externalId, this.data[0]);
    this.dataChange.next(this.data);
  }

  onDataChange() {
    this.dataChange.next(this.data);
  }

  afterDropDataChange(data) {
    this.dataChange.next(data);
  }
}

/**
 * @title Tree with flat nodes
 */
@Component({
  selector: 'app-category-details',
  templateUrl: './category-details.component.html',
  styleUrls: ['./category-details.component.scss'],
  providers: [FileDatabase],
  standalone: false
})
export class CategoryDetailsComponent implements OnInit {
  @Input() public category: any;

  @Output() isSaveDisabledEvent = new EventEmitter<any>();

  flatNodeMap = new Map<FileFlatNode, FileNode>();
  nestedNodeMap = new Map<FileNode, FileFlatNode>();
  initFlatNodeMap = new Map<FileFlatNode, FileNode>();
  initNestedNodeMap = new Map<FileNode, FileFlatNode>();

  treeControl: FlatTreeControl<FileFlatNode>;
  treeFlattener: MatTreeFlattener<FileNode, FileFlatNode>;
  dataSource: MatTreeFlatDataSource<FileNode, FileFlatNode>;
  // expansion model tracks expansion state
  expansionModel = new SelectionModel<string>(true);
  dragging = false;
  expandTimeout: any;
  expandDelay = 1000;
  isCategorySelected: boolean = false;

  isDataLoaded: boolean = false;
  isSaveDisabled: boolean = true;
  isExpanded: boolean = false;
  recordingPermission: boolean = false;
  hasUnleashAccess: boolean = false;

  existingNewIds: number[] = [];
  initCatData: any[] = [];
  initGlobalSettings: any = {}
  queueCampaigns: any = [];
  diallerCampaigns: any = [];

  selectionType = [{
    key: 0,
    text: "Single Choice",
    value: "Single"
  }, {
    key: 1,
    text: "Multiple Choice",
    value: "Multiple"
  }];

  forcedForAgentsChoice = [{
    key: 0,
    text: "No",
    value: 0
  }, {
    key: 1,
    text: "Yes",
    value: 1
  }, {
    key: 2,
    text: "Yes, but only if call is recorded",
    value: 2
  }];

  selectionTypeChoice = 0;
  isForced = 0;

  constructor(private database: FileDatabase, private enquiryRegService: EnquiryRegService, private treeService: TreeService, private dialog: MatDialog, private i18nextPipe: I18NextPipe) {
    this.treeFlattener = new MatTreeFlattener(this.transformer, this._getLevel,
      this._isExpandable, this._getChildren);
    this.treeControl = new FlatTreeControl<FileFlatNode>(this._getLevel, this._isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    database.dataChange.subscribe(data => {
      this.dataSource.data = [];
      this.dataSource.data = data;
    });
  }

  ngOnInit(): void {
  }

  transformer = (node: FileNode, level: number) => {
    let externalId = node.externalId;
    let flatNode;
    if (this.nestedNodeMap.size === 0) {
      flatNode = new FileFlatNode();
      flatNode.expandable = (node.children && node.children.length > 0);
      flatNode.externalId = node.externalId;
      flatNode.parentNodeId = node.parentNodeId;
      flatNode.description = node.description;
      flatNode.level = level;
      flatNode.isMultiSelect = node.isMultiSelect;
      flatNode.isSensitive = node.isSensitive;
      flatNode.storageTime = node.storageTime;
      flatNode.order = node.order;
      this.flatNodeMap.set(flatNode, node);
      this.nestedNodeMap.set(node, flatNode);
    } else {
      let nodeExists = false;
      for (let [key, value] of this.nestedNodeMap.entries()) {
        if (key.externalId === externalId) {
          flatNode = value;
          flatNode.expandable = (node.children && node.children.length > 0);
          flatNode.externalId = node.externalId;
          flatNode.parentNodeId = node.parentNodeId;
          flatNode.description = node.description;
          flatNode.level = level;
          flatNode.isMultiSelect = node.isMultiSelect;
          flatNode.isSensitive = node.isSensitive;
          flatNode.storageTime = node.storageTime;
          flatNode.order = node.order;
          this.flatNodeMap.set(flatNode, key);
          this.nestedNodeMap.set(key, flatNode);
          nodeExists = true;
          break;
        }
      }

      if (!nodeExists) {
        flatNode = new FileFlatNode();
        flatNode.expandable = (node.children && node.children.length > 0);
        flatNode.externalId = node.externalId;
        flatNode.parentNodeId = node.parentNodeId;
        flatNode.description = node.description;
        flatNode.level = level;
        flatNode.isMultiSelect = node.isMultiSelect;
        flatNode.isSensitive = node.isSensitive;
        flatNode.storageTime = node.storageTime;
        flatNode.order = node.order;
        this.flatNodeMap.set(flatNode, node);
        this.nestedNodeMap.set(node, flatNode);
      }
    }

    return flatNode;
  }
  private _getLevel = (node: FileFlatNode) => node.level;
  private _isExpandable = (node: FileFlatNode) => node.expandable;
  private _getChildren = (node: FileNode): Observable<FileNode[]> => observableOf(node.children);
  hasChild = (_: number, _nodeData: FileFlatNode) => _nodeData.expandable;

  ngOnChanges(changes: SimpleChanges): void {
    // Detect changes to the input property and update the component accordingly
    if (changes.category && changes.category.currentValue) {
      if (this.initGlobalSettings.description) {
        if (changes.category.previousValue) {
          this.resetGlobalSettings(changes.category.previousValue);
        } else {
          this.resetGlobalSettings(changes.category.currentValue);
        }
      }

      this.getDetails(changes.category.currentValue.categoryId);
      // this.resetData();
    }
  }

  resetData() {
    this.isDataLoaded = false;
    this.isExpanded = false;
    this.selectionTypeChoice = this.category.isMultiSelect ? 1 : 0;
    this.isForced = this.category.isForced;
    this.flatNodeMap = new Map<FileFlatNode, FileNode>();
    this.nestedNodeMap = new Map<FileNode, FileFlatNode>();
    this.initFlatNodeMap = new Map<FileFlatNode, FileNode>();
    this.initNestedNodeMap = new Map<FileNode, FileFlatNode>();
    this.expansionModel.clear();
  }

  resetGlobalSettings(category?) {
    if (category) {
      category.isDefault = this.initGlobalSettings.isDefault;
      category.isOutbound = this.initGlobalSettings.isOutbound;
      category.isMultiSelect = this.initGlobalSettings.isMultiSelect;
      category.description = this.initGlobalSettings.description;
      category.isForced = this.initGlobalSettings.isForced;
    } else {
      this.category.isDefault = this.initGlobalSettings.isDefault;
      this.category.isOutbound = this.initGlobalSettings.isOutbound;
      this.category.isMultiSelect = this.initGlobalSettings.isMultiSelect;
      this.category.description = this.initGlobalSettings.description;
      this.category.isForced = this.initGlobalSettings.isForced;
    }
  }

  setGlobalSettings() {
    this.initGlobalSettings.isDefault = this.category.isDefault;
    this.initGlobalSettings.isOutbound = this.category.isOutbound;
    this.initGlobalSettings.isMultiSelect = this.category.isMultiSelect;
    this.initGlobalSettings.description = this.category.description;
    this.initGlobalSettings.isForced = this.category.isForced;
  }

  getDetails(categoryId: number) {
    this.setGlobalSettings();
    this.resetData();
    if (categoryId !== -1) {
      let enqRegApi = this.enquiryRegService.GetCategoryDetails(categoryId)
        .subscribe((data: any) => {
          let items = data.items;
          this.hasUnleashAccess = data.unleashAccess;
          this.recordingPermission = data.recordingPermission;
          this.initCatData = _.cloneDeep(items);
          this.database.initialize(items, this.category);
          this.isSaveDisabled = true;
          this.isDataLoaded = true;
          this.isSaveDisabledEvent.emit(true);
          const mainNode = this.flatNodeMap.entries().next().value[0];
          if (items.length > 1) {
            this.expansionModel.toggle(categoryId.toString());
            this.treeControl.expand(mainNode);
          }
        }, error => {
          console.error('Error fetching category details:', error);
        });

      this.enquiryRegService.checkSelectedForQueue(categoryId)
        .subscribe(items => {
          this.queueCampaigns = items;
        });

      this.enquiryRegService.checkCategoryForDialer(categoryId)
        .subscribe(items => {
          this.diallerCampaigns = items;
        });
    } else {
      this.isDataLoaded = true;
    }
  }

  // DRAG AND DROP METHODS
  /**
   * This constructs an array of nodes that matches the DOM
   */
  visibleNodes(): FileNode[] {
    const result = [];
    function addExpandedChildren(node: FileNode, expanded: string[]) {
      result.push(node);
      if (expanded.includes(node.externalId.toString())) {
        node.children.map((child) => addExpandedChildren(child, expanded));
      }
    }
    this.dataSource.data.forEach((node) => {
      addExpandedChildren(node, this.expansionModel.selected);
    });
    return result;
  }

  /**
   * Handle the drop - here we rearrange the data based on the drop event,
   * then rebuild the tree.
   * */
  drop(event: CdkDragDrop<string[]>) {
    // console.log('origin/destination', event.previousIndex, event.currentIndex);

    // ignore drops outside of the tree
    if (!event.isPointerOverContainer) return;
    // construct a list of visible nodes, this will match the DOM.
    // the cdkDragDrop event.currentIndex jives with visible nodes.
    // it calls rememberExpandedTreeNodes to persist expand state
    const visibleNodes = this.visibleNodes();

    // deep clone the data source so we can mutate it
    const changedData = _.cloneDeep(this.dataSource.data);  //JSON.parse(JSON.stringify(this.dataSource.data));

    // recursive find function to find siblings of node
    function findNodeSiblings(arr: Array<any>, id: number): Array<any> {
      let result, subResult;
      arr.forEach((item, i) => {
        if (item.externalId === id) {
          result = arr;
        } else if (item.children) {
          subResult = findNodeSiblings(item.children, id);
          if (subResult) result = subResult;
        }
      });
      return result;

    }

    // determine where to insert the node
    const nodeAtDest = visibleNodes[event.currentIndex];
    const newSiblings = findNodeSiblings(changedData, nodeAtDest.externalId);
    if (!newSiblings) {
      return;
    }
    const insertIndex = newSiblings.findIndex(s => s.externalId === nodeAtDest.externalId);

    // remove the node from its old place
    const node = event.item.data;
    const siblings = findNodeSiblings(changedData, node.externalId);
    const siblingIndex = siblings.findIndex(n => n.externalId === node.externalId);
    const nodeToInsert: FileNode = siblings.splice(siblingIndex, 1)[0];
    if (nodeAtDest.externalId === nodeToInsert.externalId) {
      return;
    }

    // ensure validity of drop - must be same level
    const nodeAtDestFlatNode = this.treeControl.dataNodes.find((n) => nodeAtDest.externalId === n.externalId);
    if (nodeAtDestFlatNode.level !== node.level || nodeAtDest.parentNodeId !== node.parentNodeId) {
      return;
    }

    // insert node 
    newSiblings.splice(insertIndex, 0, nodeToInsert);
    // update order 
    for (let i = 0; i < newSiblings.length; i++) {
      newSiblings[i].order = i + 1;
      for (let [key, value] of this.nestedNodeMap.entries()) {
        if (key.externalId === newSiblings[i].externalId) {
          key.order = i + 1;
          value.order = i + 1;
          key.isUpdated = true;
          value.isUpdated = true;
          break;
        }
      }
      for (let [key, value] of this.flatNodeMap.entries()) {
        if (key.externalId === newSiblings[i].externalId) {
          key.order = i + 1;
          value.order = i + 1;
          key.isUpdated = true;
          value.isUpdated = true;
          break;
        }
      }
    }

    let parentNode = this.getParentNode(node);
    let parentFileNode = this.flatNodeMap.get(parentNode);
    this.isSaveDisabled = false;
    this.isSaveDisabledEvent.emit(false);
    // rebuild tree with mutated data
    // this.database.afterDropDataChange(parentFileNode, newSiblings);
    this.rebuildTreeForData(changedData);
  }

  /**
   * Experimental - opening tree nodes as you drag over them
   */
  dragStart() {
    this.dragging = true;
  }
  dragEnd() {
    this.dragging = false;
  }
  dragHover(node: FileFlatNode) {
    if (this.dragging) {
      clearTimeout(this.expandTimeout);
      this.expandTimeout = setTimeout(() => {
        this.treeControl.expand(node);
      }, this.expandDelay);
    }
  }
  dragHoverEnd() {
    if (this.dragging) {
      clearTimeout(this.expandTimeout);
    }
  }

  /**
   * The following methods are for persisting the tree expand state
   * after being rebuilt
   */

  rebuildTreeForData(data: any) {
    this.database.afterDropDataChange(data);
    this.expansionModel.selected.forEach((id) => {
      const node = this.treeControl.dataNodes.find((n) => n.externalId.toString() === id);
      this.treeControl.expand(node);
    });
  }

  getIdForNewItem() {
    let externalId = Math.floor(Math.random() * 1000000) + 100000;
    if (this.existingNewIds.indexOf(externalId) > -1) {
      this.getIdForNewItem();
    } else {
      this.existingNewIds.push(externalId);
      return externalId;
    }
  }

  /** Add a new item to the children array of a node */
  addNewNode(node: FileFlatNode) {
    const parentNode = this.flatNodeMap.get(node);
    const flatParentNode = this.nestedNodeMap.get(parentNode);
    const dialogRef = this.dialog.open(AddNodeDialogComponent, {
      panelClass: 'dialog-standard-pannel',
      width: "450px",
      height: "180px",
      data: {
        title: "Add",
        button: this.i18nextPipe.transform('translation:Add')
      }
    });

    dialogRef.afterClosed().subscribe(data => {
      if (data) {
        parentNode.downgradeToTopic = false;
        if (parentNode.children.length === 0 && parentNode.level !== 0 && parentNode.type === "topic") {
          parentNode.updateToCategory = true;
        }
        let externalId = this.getIdForNewItem();
        let newFileNode = this.database.insertNode(parentNode, data.description, externalId);
        if (!parentNode.children.some(c => c.externalId === newFileNode.externalId)) {
          parentNode.children.push(newFileNode);
        }
        if (this.expansionModel.selected.indexOf(node.externalId.toString()) === -1) {
          this.expansionModel.toggle(node.externalId.toString());
          let nodeToExpand = this.treeControl.dataNodes.find(dn => dn.externalId === node.externalId);
          this.treeControl.expand(nodeToExpand);
        }
        this.isSaveDisabled = false;
        this.isSaveDisabledEvent.emit(false);
      }
    });
  }

  deleteNode(node: FileFlatNode) {
    const dialogRef = this.dialog.open(DeleteSubCategoryDialogComponent, {
      panelClass: 'dialog-standard-pannel',
      width: "500px",
      height: "240px"
    });

    dialogRef.afterClosed().subscribe(data => {
      if (data?.isDelete) {
        const parentNode = this.getParentNode(node);
        const nodeToRemove = this.flatNodeMap.get(node);
        const valueNode = this.flatNodeMap.get(parentNode);
        this.database.deleteNodeAndChildren(nodeToRemove);
        this.deleteNodesRecursively(node);
        if (valueNode.children.length === 0 && valueNode.type === "subcategory") {
          valueNode.downgradeToTopic = true;
        }
        if (valueNode.children.length === 0) {
          valueNode.updateToCategory = false;
        }
        let indexAdditive = 1;
        for (let i = 0; i < valueNode.children.length; i++) {
          if (valueNode.children[i].externalId === nodeToRemove.externalId) {
            indexAdditive = 0;
            valueNode.children[i].order = -1;
          } else {
            let oldOrder = valueNode.children[i].order;
            if (oldOrder !== i + indexAdditive) {
              valueNode.children[i].order = i + indexAdditive;
              if (!valueNode.children[i].isNew) {
                valueNode.children[i].isUpdated = true;
              }
            }
          }

        }
        this.flatNodeMap.get(node).isDeleted = true;
        this.flatNodeMap.get(node).order = -1;
        this.nestedNodeMap.get(nodeToRemove).isDeleted = true;
        this.nestedNodeMap.get(nodeToRemove).order = -1;
        this.isSaveDisabled = false;
        this.isSaveDisabledEvent.emit(false);
      }
    });
  }

  editNode(node: FileFlatNode) {
    const parentNode = this.getParentNode(node);
    const fileNode = this.flatNodeMap.get(node);
    const isTopic = fileNode.children.length > 0 ? false : true;
    const isSensitive = fileNode.isSensitive === 0 ? false : true;
    const storageTime = fileNode.storageTime;
    const height =
      this.hasUnleashAccess && (this.recordingPermission || storageTime != null)
        ? '320px'
        : '250px';
    const dialogRef = this.dialog.open(EditNodeDialogComponent, {
      panelClass: 'dialog-standard-pannel',
      width: "450px",
      height: height,
      data: {
        description: node.description,
        isTopic: isTopic,
        isSensitive: isSensitive,
        storageTime: storageTime,
        recordingPermission: this.recordingPermission,
        hasUnleashAccess: this.hasUnleashAccess
      }
    });

    dialogRef.afterClosed().subscribe(data => {
      if (data) {
        const valueNode = this.flatNodeMap.get(parentNode);
        valueNode.children.find(c => c.externalId === node.externalId).description = data.description;
        valueNode.children.find(c => c.externalId === node.externalId).isSensitive = data.isSensitive ? 1 : 0;
        valueNode.children.find(c => c.externalId === node.externalId).storageTime = data.storageTime;
        valueNode.children.find(c => c.externalId === node.externalId).isUpdated = true;
        this.database.editNode(node.externalId, data.description, data.isSensitive, data.storageTime);
        this.isSaveDisabled = false;
        this.isSaveDisabledEvent.emit(false);
      }
    });
  }

  saveChanges() {
    this.isDataLoaded = false;
    let data = [];
    for (let [key, value] of this.flatNodeMap.entries()) {
      data.push(value);
    }
    this.enquiryRegService.createSubCategory(data).subscribe(res => {
      this.getDetails(this.category.categoryId);
    });
  }

  undoChanges() {
    this.resetGlobalSettings();
    this.getDetails(this.category.categoryId);
  }

  changeMainSelectionType(ev) {
    this.selectionTypeChoice = ev.key;
    this.category.isMultiSelect = ev.key === 0 ? false : true;
    for (let [key, value] of this.flatNodeMap.entries()) {
      if (key.level === 0 && key.externalId === this.category.categoryId) {
        key.isMultiSelect = ev.key;
        value.isMultiSelect = ev.key;
        key.isUpdated = true;
        value.isUpdated = true;
        break;
      }
    }
    for (let [key, value] of this.nestedNodeMap.entries()) {
      if (key.level === 0 && key.externalId === this.category.categoryId) {
        key.isMultiSelect = ev.key;
        value.isMultiSelect = ev.key;
        key.isUpdated = true;
        value.isUpdated = true;
        break;
      }
    }
    this.isSaveDisabled = false;
    this.isSaveDisabledEvent.emit(false);
  }

  changeForcedForAgents(ev) {
    this.isForced = ev.key;
    this.category.isForced = ev.value;
    for (let [key, value] of this.flatNodeMap.entries()) {
      if (key.level === 0 && key.externalId === this.category.categoryId) {
        key.isForced = ev.key;
        value.isForced = ev.key;
        key.isUpdated = true;
        value.isUpdated = true;
        break;
      }
    }
    for (let [key, value] of this.nestedNodeMap.entries()) {
      if (key.level === 0 && key.externalId === this.category.categoryId) {
        key.isForced = ev.key;
        value.isForced = ev.key;
        key.isUpdated = true;
        value.isUpdated = true;
        break;
      }
    }
    this.isSaveDisabled = false;
    this.isSaveDisabledEvent.emit(false);
  }

  changeNodeSelectionType(node: FileFlatNode) {
    let fileNode = this.flatNodeMap.get(node);
    this.flatNodeMap.get(node).isMultiSelect = node.isMultiSelect;
    this.flatNodeMap.get(node).isUpdated = true;
    this.nestedNodeMap.get(fileNode).isMultiSelect = node.isMultiSelect;
    this.nestedNodeMap.get(fileNode).isUpdated = true;
    this.isSaveDisabled = false;
    this.isSaveDisabledEvent.emit(false);
  }

  onMainCategoryNameChange(ev) {
    this.category.description = ev;
    for (let [key, value] of this.flatNodeMap.entries()) {
      if (key.level === 0 && key.externalId === this.category.categoryId) {
        key.description = ev;
        value.description = ev;
        key.isUpdated = true;
        value.isUpdated = true;
        break;
      }
    }
    for (let [key, value] of this.nestedNodeMap.entries()) {
      if (key.level === 0 && key.externalId === this.category.categoryId) {
        key.description = ev;
        value.description = ev;
        key.isUpdated = true;
        value.isUpdated = true;
        break;
      }
    }
    this.isSaveDisabled = false;
    this.isSaveDisabledEvent.emit(false);
    this.database.onDataChange();
  }

  setMainCategoryAsUpdated(ev) {
    for (let [key, value] of this.flatNodeMap.entries()) {
      if (key.level === 0 && key.externalId === this.category.categoryId) {
        key.description = ev;
        value.description = ev;
        key.isUpdated = true;
        value.isUpdated = true;
        break;
      }
    }
    for (let [key, value] of this.nestedNodeMap.entries()) {
      if (key.level === 0 && key.externalId === this.category.categoryId) {
        key.description = ev;
        value.description = ev;
        key.isUpdated = true;
        value.isUpdated = true;
        break;
      }
    }
  }

  onGlobalSettingsChange(event, type) {
    let settingType = Number(GlobalSettings[type]);
    let isWarning: boolean = false;
    if (this.category.isOutbound === true && settingType !== 2 && this.diallerCampaigns.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      isWarning = true;
      const dialogRef = this.dialog.open(WarningDialogComponent, {
        panelClass: 'dialog-standard-pannel',
        width: "900px",
        height: "250px",
        data: { items: this.diallerCampaigns, isDiallerCheck: true }
      });

      dialogRef.afterClosed().subscribe(data => {
        this.category.isDefault = this.category.isDefault;
        this.category.isOutbound = this.category.isOutbound;
      });
    }

    if (this.category.isOutbound === false && settingType === 2 && this.queueCampaigns.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      isWarning = true;
      const dialogRef = this.dialog.open(WarningDialogComponent, {
        panelClass: 'dialog-standard-pannel',
        width: "900px",
        height: "250px",
        data: { items: this.queueCampaigns, isDiallerCheck: false }
      });

      dialogRef.afterClosed().subscribe(data => {
        this.category.isDefault = this.category.isDefault;
        this.category.isOutbound = this.category.isOutbound;
      });
    }

    if (!isWarning) {
      switch (settingType) {
        case 0:
          this.category.isDefault = false;
          this.category.isOutbound = false;
          break;
        case 1:
          this.category.isDefault = true;
          this.category.isOutbound = false;
          break;
        case 2:
          this.category.isDefault = false;
          this.category.isOutbound = true;
          break;
      }

      for (let [key, value] of this.flatNodeMap.entries()) {
        if (key.level === 0 && key.externalId === this.category.categoryId) {
          key.isDefault = this.category.isDefault ? 1 : 0;
          value.isOutbound = this.category.isOutbound ? 1 : 0;
          key.isUpdated = true;
          value.isUpdated = true;
          break;
        }
      }
      for (let [key, value] of this.nestedNodeMap.entries()) {
        if (key.level === 0 && key.externalId === this.category.categoryId) {
          key.isDefault = this.category.isDefault ? 1 : 0;
          value.isOutbound = this.category.isOutbound ? 1 : 0;
          key.isUpdated = true;
          value.isUpdated = true;
          break;
        }
      }

      this.isSaveDisabled = false;
      this.isSaveDisabledEvent.emit(false);
    }
  }

  toggleExpand() {
    this.isExpanded = true;
    for (let [key, value] of this.flatNodeMap.entries()) {
      if (value.children.length > 0) {
        this.expansionModel.toggle(value.externalId.toString());
        this.expansionModel.selected.push(value.externalId.toString());
        this.treeControl.expand(key);
      }
    }
    this.expansionModel.selected.push(this.category.categoryId.toString());

  }

  toggleCollapse() {
    this.isExpanded = false;
    for (let [key, value] of this.flatNodeMap.entries()) {
      if (value.children.length > 0) {
        if (key.externalId !== this.category.categoryId) {
          this.expansionModel.toggle(value.externalId.toString());
          this.treeControl.collapse(key);
        }
      }
    }

    if (this.expansionModel.selected.indexOf(this.category.categoryId.toString()) < 0) {
      this.expansionModel.selected.push(this.category.categoryId.toString());
    }
  }

  /**
   * Not used but you might need this to programmatically expand nodes
   * to reveal a particular node
   */
  private expandNodesById(flatNodes: FileFlatNode[], ids: string[]) {
    if (!flatNodes || flatNodes.length === 0) return;
    const idSet = new Set(ids);
    return flatNodes.forEach((node) => {
      if (idSet.has(node.externalId.toString())) {
        this.treeControl.expand(node);
        let parent = this.getParentNode(node);
        while (parent) {
          this.treeControl.expand(parent);
          parent = this.getParentNode(parent);
        }
      }
    });
  }

  private getParentNode(node: FileFlatNode): FileFlatNode | null {
    const currentLevel = node.level;
    if (currentLevel < 1) {
      return null;
    }
    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;
    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];
      if (currentNode.level < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  private deleteNodesRecursively(node: FileFlatNode) {
    let flatNodesToDelete: FileFlatNode[] = [];
    let nestedNodesToDelete: FileNode[] = [];
    let fileNode = this.flatNodeMap.get(node);
    for (let [key, value] of this.flatNodeMap.entries()) {
      if (value.parentNodeId === fileNode.externalId) {
        flatNodesToDelete.push(key);
        nestedNodesToDelete.push(value);
      }
    }

    if (flatNodesToDelete.length > 0) {
      for (let i = 0; i < flatNodesToDelete.length; i++) {
        this.flatNodeMap.get(flatNodesToDelete[i]).isDeleted = false;
        this.flatNodeMap.get(flatNodesToDelete[i]).isNew = false;
        this.flatNodeMap.get(flatNodesToDelete[i]).isUpdated = false;
        this.flatNodeMap.get(flatNodesToDelete[i]).downgradeToTopic = false;
        this.flatNodeMap.get(flatNodesToDelete[i]).updateToCategory = false;
        this.flatNodeMap.get(flatNodesToDelete[i]).order = -1;
        this.nestedNodeMap.get(nestedNodesToDelete[i]).isDeleted = false;
        this.nestedNodeMap.get(nestedNodesToDelete[i]).isNew = false;
        this.nestedNodeMap.get(nestedNodesToDelete[i]).isUpdated = false;
        this.nestedNodeMap.get(nestedNodesToDelete[i]).downgradeToTopic = false;
        this.nestedNodeMap.get(nestedNodesToDelete[i]).updateToCategory = false;
        this.nestedNodeMap.get(nestedNodesToDelete[i]).order = -1;
        this.deleteNodesRecursively(flatNodesToDelete[i]);
      }
    }
  }
}